<template>
  <LoadingSpinner v-if="isLoading" />
  <div v-else class="customer-detail">
    <PageHeader :title="customer?.name" :action="{ title: 'Edit', to: { name: 'EditCustomer', params: { id: customer?.id } } }" />

    <div class="grid grid-col-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      <div class="section col-span-2">
        <div class="flex items-center bg-secondary/20 p-4 rounded-md gap-4">
          <BaseAvatar class="size-1/12" />
          <!-- name & avatar -->
          <div class="flex flex-col justify-between gap-4">
            <div class="flex flex-col gap-2 ml-4">
              <h3>{{ customer?.name }}</h3>
              <div v-if="customer?.address" class="flex gap-2 items-center"><span class="i-heroicons-map-pin" />{{ customer?.address }}</div>
              <div class="flex gap-4">
                <div v-if="company?.name" class="flex gap-2 items-center"><span class="i-heroicons-building-office" />{{ company?.name }}</div>
                <div v-if="customer?.email" class="flex gap-2 items-center"><span class="i-heroicons-envelope"></span>{{ customer?.email }}</div>
                <div v-if="customer?.phone" class="flex gap-2 items-center"><span class="i-heroicons-phone" />{{ customer?.phone }}</div>
              </div>
            </div>
          </div>
        </div>
        <AppStats :items="customerStats" class="mt-8" />

        <BaseTable
          :fields="tableFields"
          :items="projects ?? []"
          class="mt-8"
        >
          <template #createdAt-data="data">
            <span>{{ new Date(data.row.createdAt).toLocaleDateString() }}</span>
          </template>
          <template #name-data="data">
            <router-link
              :to="{ name: 'ShowProject', params: { customerId: customer?.id, projectId: data.row.id } }"
            >
              {{ data.row.name }}
            </router-link>
          </template>
        </BaseTable>

        <!-- tabs -->
      </div>
      <div class="sidebar">
        <div class="section p-4">
          <h3 class="section-title">Recent Activities</h3>
        </div>
      </div>
    </div>

    <!-- Invoices -->
    <!-- <div v-if="!isCompany">
      <h3>Invoices</h3>
      <b-card>
        <b-row class="justify-content-center">
          <apexchart
            width="400"
            type="donut"
            :options="chartOptions"
            :series="series"
          />
        </b-row>
      </b-card>
    </div> -->
    <!-- manage customer -->
    <!-- <div class="mt-5">
      <h3>Manage customer</h3>
      <b-card>
        <b-row class="mt-2">
          <b-col cols="3">
            <div class="text-muted">
              Name
            </div>
            <div>{{ customer?.name }}</div>
          </b-col>
          <b-col
            v-if="!isCompany"
            cols="3"
          >
            <div class="text-muted">
              Company
            </div>
            <div>{{ getCompanyName(customer?.companyId) }}</div>
          </b-col>
          <b-col cols="3">
            <div class="text-muted">
              Address
            </div>
            <div>{{ customer?.address }}</div>
          </b-col>
          <b-col cols="3">
            <div
              v-if="customer?.billing"
              class="text-muted"
            >
              Billing Address
            </div>
            <div>{{ customer?.billing }}</div>
          </b-col>
        </b-row>

        <b-row class="mt-3">
          <b-col cols="3">
            <div class="text-muted">
              Email
            </div>
            <div>{{ customer?.email }}</div>
          </b-col>
          <b-col cols="3">
            <div class="text-muted">
              Phone
            </div>
            <div>{{ customer?.phone }}</div>
          </b-col>
        </b-row>
      </b-card>
    </div> -->

    <!-- projects list -->
    <!-- <div
      v-if="!isCompany"
      class="mt-5"
    >
      <h3>Projects</h3>
      <b-card>
        <b-table
          :items="projects"
          :fields="fields"
          head-variant="light"
          responsive
        >
          <template #cell(status)="data">
            <b-badge variant="success">
              {{ data.item.status }}
            </b-badge>
          </template>
          <template #cell(name)="data">
            <router-link
              :to="{ name: 'ShowProject', params: { customerId: customer?.id, projectId: data.item.id } }"
            >
              {{ data.value }}
            </router-link>
          </template>
        </b-table>
      </b-card>
    </div> -->
  </div>
</template>

<script lang="ts" setup>
import { toValue, computed, ref, Ref } from 'vue'
import { useHead } from '@unhead/vue'
import { useQuery } from '@tanstack/vue-query'
import { useRoute } from 'vue-router'
import CustomerService from '@/services/CustomerService'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import PageHeader from '@/components/app/AppPageHeader.vue'
import BaseAvatar from '@/components/base/BaseAvatar.vue'
import BaseTable from '@/components/base/BaseTable.vue'
import AppStats,  { StatItem } from '@/components/app/AppStats.vue'
import { INVOICE_STATUS } from '@/models/invoice'
import { currency } from '@/utils'

useHead({
  title: 'Show Customer'
})

const $route = useRoute()
const customerId = Array.isArray($route.params.id) ? $route.params.id[0] : $route.params.id
const customerStats: Ref<StatItem[]> = ref([
  { name: 'QBO status', value: 'not synced' },
  { name: 'Projects value', value: 0 },
  { name: 'Outstanding invoices', value: 0 },
  { name: 'Overdue invoices', value: 0 },
])
const tableFields = [
  { key: 'createdAt', label: 'Date', sortable: true },
  { key: 'name', label: 'Name' },
  { key: 'status', label: 'Status' },
]

const customerService = new CustomerService()
const { data: customer, isLoading } = useQuery({
  queryKey: ['customer', customerId],
  queryFn: async () => {
    const customer = await customerService.find(customerId)
    if (customer?.qboId) {
      const stat = customerStats.value.find(stat => stat.name === 'QBO status')
      if (stat) stat.value = 'synced'
    }

    return customer
  }
})

const companyId = computed(() => toValue(customer)?.companyId)

const { data: company } = useQuery({
  queryKey: ['company', companyId],
  queryFn: () => customerService.find(companyId.value),
  enabled: !!companyId?.value
})

const { data: projects } = useQuery({
  queryKey: ['projects', customerId],
  queryFn:  () => customerService.getProjects(customerId)
})

useQuery({
  queryKey: ['invoices', `${customerId}-invoices`],
  queryFn: async () => {
    const invoices = await customerService.getInvoices(customerId)

    const outstandingTotal = invoices.reduce((acc, invoice) => {
      if (invoice.status !== INVOICE_STATUS.OUTSTANDING) return acc
      return acc + invoice.total
    }, 0)

    const overdueTotal = invoices.reduce((acc, invoice) => {
      if (invoice.status !== INVOICE_STATUS.OVERDUE) return acc
      return acc + invoice.total
    }, 0)

    customerStats.value.find(stat => stat.name === 'Outstanding invoices').value = currency(outstandingTotal)
    customerStats.value.find(stat => stat.name === 'Overdue invoices').value = currency(overdueTotal)

    return invoices
  }
})

</script>
